// Copyright 2017 Sorama B.V.

import { autoinject, bindable, bindingMode, computedFrom, TaskQueue } from "aurelia-framework";
import { Rect } from "controls/plot/IPlotPart";
import { SpectrogramBasePlot } from "controls/plot/waterfall-plot/SpectrogramBasePlot";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { IColorMap } from "services/ColorMap/IColorMap";
import { Generic } from "units/Generic";
import { Unit, UnitValue } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";

@bindable({ name: "colormap", defaultBindingMode: bindingMode.oneWay })
@autoinject
export class SpectrogramPlot extends SpectrogramBasePlot {
    //#region Bindables
    @bindable
    private spectra: UnitSpectrum[];

    @bindable({ changeHandler: "fixedExtremaChanged", defaultBindingMode: bindingMode.oneWay })
    protected fixedMinimum: UnitValue;

    @bindable({ changeHandler: "fixedExtremaChanged", defaultBindingMode: bindingMode.oneWay })
    protected fixedMaximum: UnitValue;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected dataMinimum: UnitValue;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected dataMaximum: UnitValue;

    @bindable
    protected displayUnit: Unit = Generic.unitless;

    @bindable
    protected colormap: IColorMap;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    public xAxisBoundStart: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    public xAxisBoundEnd: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    public yAxisBoundStart: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    public yAxisBoundEnd: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected xAxisStart: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected xAxisEnd: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected yAxisStart: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected yAxisEnd: number;

    //#endregion
    @computedFrom("spectra")
    protected get hasData(): boolean {
        return this.spectra !== undefined && this.spectra.length > 0;
    }

    constructor(resizeListener: ResizeListener, taskQueue: TaskQueue) {
        super(resizeListener, taskQueue);
    }

    //#region Change handlers

    private spectraChanged(newValue: UnitSpectrum[], oldValue: UnitSpectrum[]): void {
        // Update data texture
        super.clearSpectra();

        let minX = Number.MAX_VALUE;
        let maxX = -Number.MAX_VALUE;

        let minY = Number.MAX_VALUE;
        let maxY = -Number.MAX_VALUE;

        if (newValue !== undefined && newValue.length > 0) {
            // Determine new min max
            minX = Number.POSITIVE_INFINITY;
            maxX = Number.NEGATIVE_INFINITY;

            minY = Number.POSITIVE_INFINITY;
            maxY = Number.NEGATIVE_INFINITY;

            for (let spectrum of newValue) {
                let time = (spectrum.properties.timeRange.from + spectrum.properties.timeRange.to) / 2;

                if (time < minX) minX = time;

                if (time > maxX) maxX = time;

                let firstPoint = spectrum.data[0];
                let lastPoint = spectrum.data[spectrum.data.length - 1];

                if (firstPoint.x < minY) minY = firstPoint.x;

                if (lastPoint.x > maxY) maxY = lastPoint.x;
            }

            // Update bounds the to new value
            this.viewPortBounds = new Rect(minX, minY, maxX - minX, maxY - minY);

            // Measure
            this.requestViewPort();

            // Add spectra
            super.addSpectra(newValue);
        } else {
            // No new data, set default min max
            this.viewPortBounds = new Rect(
                this.xAxisStart,
                this.yAxisStart,
                this.xAxisEnd - this.xAxisStart,
                this.yAxisEnd - this.yAxisStart
            );
        }

        super.redraw();
    }

    private displayUnitChanged(newValue: Unit, oldValue: Unit) {
        if (newValue) {
            // Trigger spectrum update
            this.spectraChanged(this.spectra, this.spectra);
        } else {
            super.clearSpectra();
            super.redraw();
        }
    }

    //#endregion
}
